import { APIEndpoints } from '@/@admin/types/constants';
import { AdminToolsParams, License, UpdateResponse } from '@/@admin/types/entities';
import { buildCacheKey } from '@/@admin/utils';
import { ERROR_MESSAGES } from '@/@cms/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for license management
 */
export class LicenseService extends BaseService {
    constructor() {
        super(portalApi);
    }

    async getLicenses(params: AdminToolsParams): Promise<License[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchLicenses', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<{
                hasError: boolean;
                message?: string;
                data?: License[];
            }>(APIEndpoints.CLIENT_LICENSE, { params });

            if (response?.hasError || !Array.isArray(response?.data)) {
                const msg = response?.message ?? 'No licenses data received from the server';
                tracking.errorWithSource(new Error(msg), 'studio.LicenseService.getLicenses');
                throw new Error(ERROR_MESSAGES.API_ERROR(msg));
            }

            this.setCachedData(cacheKey, response.data);
            return response.data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get licenses: ${errorMessage}`),
                'studio.LicenseService.getLicenses',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to get licenses: ${errorMessage}`));
        }
    }

    async updateLicense(orgLicenseId: number, data: Partial<License>): Promise<UpdateResponse> {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.CLIENT_LICENSE}/${orgLicenseId}`,
                data,
            );

            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'Failed to update license'),
                    'studio.LicenseService.updateLicense',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(response.message || 'Failed to update license'),
                );
            }

            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update license: ${errorMessage}`),
                'studio.LicenseService.updateLicense',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to update license: ${errorMessage}`));
        }
    }
}

export const licenseService = new LicenseService();
